
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useAnalytics } from "@/hooks/useAnalytics";
import { Trash2 } from "lucide-react";
import { DeleteAccountModal } from "@/components/DeleteAccountModal";

const PersonalizationPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { trackPersonalization, trackError } = useAnalytics();
  
  const [instructions, setInstructions] = useState("");
  const [learningGoals, setLearningGoals] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleComplete = async () => {
    if (!user) {
      toast({
        title: "Error",
        description: "You must be logged in to save personalization data.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          interaction_preferences: instructions,
          goals: learningGoals,
        })
        .eq('id', user.id);

      if (error) {
        console.error('Error updating profile:', error);
        trackError('personalization_save_failed', error.message);
        toast({
          title: "Error",
          description: "Failed to save your personalization preferences. Please try again.",
          variant: "destructive",
        });
        return;
      }

      // Track successful personalization completion
      trackPersonalization({
        goals: learningGoals,
        interaction_preferences: instructions
      });

      toast({
        title: "Welcome to Studibudi!",
        description: "Your personalization has been saved successfully.",
      });
      
      navigate("/");
    } catch (error) {
      console.error('Unexpected error:', error);
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = () => {
    navigate("/");
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-0" style={{
      backgroundImage: "url('/lovable-uploads/aa2d0358-95e4-4b49-8ae4-3bf7b7700431.png')",
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat"
    }}>
      <div className="w-full max-w-lg bg-white/70 backdrop-blur-3xl mx-0 my-0 rounded-lg shadow-lg px-[40px] py-[40px]" style={{backdropFilter: 'blur(40px)'}}>
        {/* Header */}
        <div className="text-center mb-8">
          <img 
            src="/lovable-uploads/1974cc86-084c-4b09-84c2-9dd606c572c1.png" 
            alt="Studibudi" 
            className="h-8 mx-auto"
          />
        </div>

        {/* Personalization Form */}
        <div className="space-y-6">
          <div className="text-center">
            <h2 className="text-xl font-medium text-gray-900 mb-2">Let's personalize your experience</h2>
            <p className="text-gray-600 text-sm mb-8">
              Tell us about yourself to get better, more personalized responses
            </p>
          </div>

          <div className="space-y-4">

            <div>
              <Label htmlFor="instructions" className="text-sm font-medium text-gray-700 mb-3 block">
                Instructions & General Preferences
              </Label>
              <Textarea
                id="instructions"
                placeholder="Instructions, approaches, or preferences to keep in mind"
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                className="w-full h-20 px-4 bg-slate-100 border border-slate-200 rounded-lg text-gray-800 placeholder-gray-400 resize-none"
                disabled={isLoading}
              />
            </div>

            <div>
              <Label htmlFor="goals" className="text-sm font-medium text-gray-700 mb-3 block">
                Learning Goals
              </Label>
              <Textarea
                id="goals"
                placeholder="What would you like to achieve"
                value={learningGoals}
                onChange={(e) => setLearningGoals(e.target.value)}
                className="w-full h-20 px-4 bg-slate-100 border border-slate-200 rounded-lg text-gray-800 placeholder-gray-400 resize-none"
                disabled={isLoading}
              />
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between pt-4">
              <Button 
                variant="outline" 
                onClick={handleSkip}
                disabled={isLoading}
                className="h-12 px-6 border border-gray-200 hover:bg-gray-50 bg-white rounded-lg shadow-none"
              >
                Skip for now
              </Button>
              <Button 
                onClick={handleComplete}
                disabled={isLoading}
                className="h-12 px-6 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg border-0 shadow-none"
              >
                {isLoading ? "Saving..." : "Complete Setup"}
              </Button>
            </div>
          </div>

          {/* Delete Account Section */}
          <div className="border-t border-gray-200/50 pt-6 mt-8">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-red-800 mb-1">Delete Account</p>
                  <p className="text-xs text-red-600">
                    Permanently remove your account
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDeleteModal(true)}
                  className="border-red-300 text-red-700 hover:bg-red-50 hover:border-red-400"
                  disabled={isLoading}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8">
          <div className="flex justify-between items-center text-sm text-gray-400 mb-4">
            <div className="flex space-x-2">
              <Link to="/terms" className="hover:text-gray-600 transition-colors">
                Terms of Use
              </Link>
              <Link to="/privacy" className="hover:text-gray-600 transition-colors">
                Privacy Policy
              </Link>
            </div>
            <p className="text-xs">© 2025 Studibudi</p>
          </div>
        </div>
      </div>

      <DeleteAccountModal 
        isOpen={showDeleteModal} 
        onClose={() => setShowDeleteModal(false)} 
      />
    </div>
  );
};

export default PersonalizationPage;
